import { Box, Typography } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { API_GET, DBRequest, useConfig } from '../../api/api';
import { useTranslation } from 'react-i18next';
import ExoDialog from '../exo/ExoDialog';
import TimeEntryReviewStep from './TimeEntryReviewStep';
import Loader from '../special/Loader';

/**
 * Dialog for editing time billing positions after creation.
 * Allows users to edit time entry comments, adjust times, and exclude entries.
 */
const SelectTimeBillingEdit = ({
  open,
  onClose,
  position,
  timeTrackingData,
  onSave,
}) => {
  const config = useConfig();
  const { t } = useTranslation();

  const [timeTrackings, setTimeTrackings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get time tracking IDs from position
  // Fallback: If position.timeTrackingIds is empty (legacy drafts), try to reconstruct
  // from timeTrackingData.billingConfig.wagePresets by matching position to preset
  const timeTrackingIds = useMemo(() => {
    // First try direct IDs from position
    if (position?.timeTrackingIds?.length > 0) {
      return position.timeTrackingIds;
    }

    // Fallback: Try to get IDs from wagePresets by matching position
    const wagePresets = timeTrackingData?.billingConfig?.wagePresets;
    if (!wagePresets?.length || !position) {
      return [];
    }

    // Try to find matching preset by wagePresetId first
    if (position.wagePresetId) {
      const matchedPreset = wagePresets.find(
        (wp) => String(wp.id) === String(position.wagePresetId)
      );
      if (matchedPreset?.timeTrackingIds?.length > 0) {
        return matchedPreset.timeTrackingIds;
      }
    }

    // Fallback: Match by price and label in name
    const positionPrice = parseFloat(position.price);
    const positionName = position.name || '';

    const matchedPreset = wagePresets.find((wp) => {
      const presetWage = parseFloat(wp.wage);
      const presetLabel = wp.label || '';
      // Match if price equals wage AND position name contains preset label
      return (
        Math.abs(positionPrice - presetWage) < 0.01 &&
        presetLabel &&
        positionName.includes(presetLabel)
      );
    });

    if (matchedPreset?.timeTrackingIds?.length > 0) {
      return matchedPreset.timeTrackingIds;
    }

    return [];
  }, [position, timeTrackingData]);

  // Get initial adjustments for this position from timeTrackingData
  const initialAdjustments = useMemo(() => {
    if (!timeTrackingData || !timeTrackingIds.length) return {};

    const ids = timeTrackingData.ids || [];
    const adjustments = timeTrackingData.adjustments || [];
    const adjustmentsMap = {};

    // Handle both array format (indexed by position) and object format (keyed by ID)
    const isArrayFormat = Array.isArray(adjustments);

    timeTrackingIds.forEach((ttId) => {
      let adj = null;

      if (isArrayFormat) {
        // Array format: find index of ID and get adjustment at that index
        const index = ids.findIndex(
          (id) => id === ttId || String(id) === String(ttId)
        );
        if (index >= 0) {
          adj = adjustments[index];
        }
      } else {
        // Object format: use ID as key
        adj = adjustments[ttId] || adjustments[String(ttId)];
      }

      if (adj && Object.keys(adj).length > 0) {
        adjustmentsMap[ttId] = {
          comment: adj.comment,
          time_start: adj.timeStart || adj.time_start,
          time_end: adj.timeEnd || adj.time_end,
          exclude_from_report:
            adj.excludeFromReport || adj.exclude_from_report || false,
        };
      }
    });

    return adjustmentsMap;
  }, [timeTrackingData, timeTrackingIds]);

  // Get billing config for the position
  const billingConfig = useMemo(() => {
    return timeTrackingData?.billingConfig || {};
  }, [timeTrackingData]);

  // Build preset data for TimeEntryReviewStep (for display purposes)
  const selectedPresets = useMemo(() => {
    if (!position || !billingConfig.wagePresets) {
      // If no wage presets, create a single pseudo-preset
      return [
        {
          id: 'single',
          label: position?.name || t('Time Entries'),
          wage: position?.price || 0,
          timeTrackingIds: timeTrackingIds,
          color: '#2196f3',
        },
      ];
    }

    // Find the wage preset for this position (use String comparison for type safety)
    // Guard: both wp.id and position.wagePresetId must be defined to avoid
    // String(undefined) === String(undefined) matching
    const wagePreset = billingConfig.wagePresets.find(
      (wp) =>
        wp.id &&
        position.wagePresetId &&
        String(wp.id) === String(position.wagePresetId),
    );

    if (wagePreset) {
      return [
        {
          ...wagePreset,
          timeTrackingIds: timeTrackingIds,
          color: '#2196f3',
        },
      ];
    }

    return [
      {
        id: 'single',
        label: position?.name || t('Time Entries'),
        wage: position?.price || 0,
        timeTrackingIds: timeTrackingIds,
        color: '#2196f3',
      },
    ];
  }, [position, billingConfig, timeTrackingIds, t]);

  // Load time trackings when dialog opens
  useEffect(() => {
    if (!open || !timeTrackingIds.length) {
      setTimeTrackings([]);
      return;
    }

    setIsLoading(true);

    DBRequest({
      config,
      path: `timetrackings?ids=${timeTrackingIds.join(',')}`,
      method: API_GET,
      onResponse: (res) => {
        setTimeTrackings(res.data || []);
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  }, [open, config, timeTrackingIds.join(',')]);

  // Handle save from TimeEntryReviewStep
  function handleConfirm(newAdjustments, presetHoursMap) {
    // Calculate new amount (hours) for the position
    // Use the preset ID from selectedPresets to get the correct hours value
    const presetId = selectedPresets[0]?.id;
    const newHours =
      presetHoursMap &&
      presetId !== undefined &&
      presetHoursMap[presetId] !== undefined
        ? presetHoursMap[presetId]
        : position.amount;

    // Keep all timeTrackingIds — excluded entries are only marked via
    // adjustments (exclude_from_report), not removed from the list
    onSave({
      position: {
        ...position,
        timeTrackingIds: timeTrackingIds,
        amount: parseFloat(newHours.toFixed(2)),
      },
      adjustments: newAdjustments,
    });
  }

  return (
    <ExoDialog open={open} onClose={onClose} limitWidth maxWidth="1200px">
      <Loader active={isLoading} />
      {!isLoading && timeTrackings.length > 0 && (
        <TimeEntryReviewStep
          selectedPresets={selectedPresets}
          startDate={billingConfig.startDate || position?.dateRangeStart}
          endDate={billingConfig.endDate || position?.dateRangeEnd}
          onBack={onClose}
          onConfirm={handleConfirm}
          config={config}
          editMode={true}
          initialAdjustments={initialAdjustments}
        />
      )}
      {!isLoading && timeTrackings.length === 0 && (
        <Box className="flex flex-col items-center justify-center h-64 gap-4">
          <Typography variant="h6" sx={{ opacity: 0.6 }}>
            {t('No time entries found')}
          </Typography>
        </Box>
      )}
    </ExoDialog>
  );
};

export default SelectTimeBillingEdit;
